'use client'

import React, { useRef, useState } from 'react'
import { Play } from 'lucide-react'
import { cn } from '@/utilities/ui'
import { getBunnyGuidForFilename, getBunnyMp4Url, getBunnyThumbnailUrl } from '@/components/Video/bunny'

/**
 * Click-to-play video panel — Figma `Video` 4093:73284 (Careers "Life at Wilson"):
 * the video sits paused on its first frame with the canonical `icon button`
 * (36×36, theme/accent, radius sm, 16px Lucide PLAY glyph) centred on top.
 * Clicking plays with native controls; pausing brings the button back.
 */
export function PlayableVideoMedia({ url, className }: { url: string; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [started, setStarted] = useState(false)

  // Uploads mirrored to Bunny Stream play the CDN MP4 and get the real
  // thumbnail as poster; the container is preset to 16:9 either way so the
  // page never jumps while metadata loads (Steve 2026-08-19).
  const filename = decodeURIComponent(url.split('/').pop() || '')
  const bunnyGuid = getBunnyGuidForFilename(filename)
  const src = bunnyGuid ? getBunnyMp4Url(bunnyGuid) : url
  const poster = bunnyGuid ? getBunnyThumbnailUrl(bunnyGuid) : undefined

  return (
    <div className={cn('relative aspect-video w-full overflow-hidden bg-tertiary', className)}>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        preload="metadata"
        playsInline
        controls={playing}
        onLoadedMetadata={(e) => {
          // Some videos fade in from black — nudge the paused frame forward so
          // the poster isn't a black square (skipped when a real poster exists)
          const v = e.currentTarget
          if (!poster && v.duration > 3) v.currentTime = 1.5
        }}
        onPlay={(e) => {
          // First play starts from the beginning, not the nudged poster frame
          if (!started) {
            setStarted(true)
            if (!poster) e.currentTarget.currentTime = 0
          }
          setPlaying(true)
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {!playing && (
        <button
          type="button"
          onClick={() => videoRef.current?.play()}
          aria-label="Play video"
          className="absolute inset-0 flex cursor-pointer items-center justify-center"
        >
          <span className="flex size-9 items-center justify-center rounded-sm bg-accent text-accent-foreground transition-transform duration-200 hover:scale-105">
            <Play className="size-4 fill-current" aria-hidden="true" />
          </span>
        </button>
      )}
    </div>
  )
}
